import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch } from 'react-router-dom';
import styled from 'styled-components';

import { height, px2rem } from '../orange-ui/styles/variables';
import device from '../helpers/breakpoints';

import ProtectedRoute from './ProtectedRoute';
import { path, routesCanGoBack, stepOrder } from '../conf/route.conf';
import WordingService from '../services/WordingService';
import LetterView from '../containers/LetterView';
import LoginView from '../containers/LoginView';
import ContactView from '../containers/ContactView';
import OtpView from '../containers/OtpView';
import ErrorView from '../containers/ErrorView';
import ConfirmView from '../containers/ConfirmView';
import ResetView from '../containers/ResetView';
import ChoiceView from '../containers/ChoiceView';

const Wrapper = styled.div`
  margin-bottom: ${px2rem(height.marginBottom.medium)};
  
  @media ${device.mobile} {
     margin-bottom: ${px2rem(height.marginBottom.small)};
  }
`;

class AppRouter extends Component {
  /**
   * Update the component or not when the actual route allows it
   * Hack the history to block navigation when necessary
   * @param nextProps
   * @return {boolean}
   */
  shouldComponentUpdate(nextProps) {
    const { location } = this.props;

    return this.navigationPattern(location, nextProps);
  }

  /**
   * Set the HTML title and set reset scroll position on route update
   */
  componentDidUpdate(prevProps) {
    const { location } = this.props;

    if (prevProps.location !== location) {
      const { pathname } = location;
      document.title = `${WordingService.getWording('htmlTitle', pathname, true)}`;
      window.scrollTo(0, 0);
    }
  }

  /**
   * Navigation pattern
   * @param location
   * @param nextProps
   * @return {boolean}
   */
  navigationPattern = (location, nextProps) => {
    const {
      history, resetStore, deleteWhiteListPage,
    } = this.props;

    if (location.pathname !== nextProps.location.pathname) {
      // On forward navigation
      if (this.isForwardAction(nextProps.history.action, location.pathname, nextProps.location.pathname)) {
        // Go back to avoid forward navigation
        history.goBack();
        return false;
      }

      // On back navigation
      if (this.isBackAction(nextProps.history.action, location.pathname, nextProps.location.pathname)) {
        // Push to actual location when the actual location can't go back
        if (!this.routeCanGoBack(location.pathname)) {
          history.push(location.pathname);
          return false;
        }

        // Push to login page when the actual location is error page
        if (location.pathname === path.ERROR) {
          resetStore();
          history.push(path.LOGIN);
          return false;
        }

        // Reset the store when go back to the login step
        if (nextProps.location.pathname === path.LOGIN) {
          resetStore();
          return true;
        }

        // Delete from whitelist the actual location
        deleteWhiteListPage(location);
      }
    }
    return true;
  };

  /**
   * Boolean back navigation router action
   * @param action
   * @param pathname
   * @param nextPathname
   * @return {boolean}
   */
  isBackAction = (action, pathname, nextPathname) => (action === 'POP' && (stepOrder[nextPathname] < stepOrder[pathname]));

  /**
   * Boolean forward navigation router action
   * @param action
   * @param pathname
   * @param nextPathname
   * @return {boolean}
   */
  isForwardAction = (action, pathname, nextPathname) => (action === 'POP' && (stepOrder[nextPathname] > stepOrder[pathname]));

  /**
   * Boolean validator
   * @param pathname
   * @return {string | undefined}
   */
  routeCanGoBack = pathname => routesCanGoBack.find(route => route === pathname);

  render() {
    return (
      <Wrapper>
        <Switch>
          <ProtectedRoute exact path={path.LOGIN} component={LoginView} />
          <ProtectedRoute exact path={path.CHOICE} component={ChoiceView} />
          <ProtectedRoute exact path={path.LETTER} component={LetterView} />
          <ProtectedRoute exact path={path.OTC} component={OtpView} />
          <ProtectedRoute exact path={path.PASSWORD} component={ResetView} />
          <ProtectedRoute exact path={path.CONTACT} component={ContactView} />
          <ProtectedRoute exact path={path.CONFIRM} component={ConfirmView} />
          <Route exact path={path.ERROR} component={ErrorView} />
        </Switch>
      </Wrapper>
    );
  }
}

AppRouter.propTypes = {
  /** Action to delete a specific step from whitelist pages */
  deleteWhiteListPage: PropTypes.func.isRequired,
  /** Action to back to the first step */
  resetStore: PropTypes.func.isRequired,
  /** Object browser history */
  history: PropTypes.shape().isRequired,
  /** Object router location */
  location: PropTypes.shape().isRequired,
};

export default AppRouter;
